import { Swords } from 'lucide-react';

/**
 * TTRPG System definitions.
 * Each entry has:
 *   id          – stable key stored on the scene
 *   name        – display name
 *   buttonType  – AwesomeButton type string
 *   buttonIcon  – Lucide component or PNG key string
 *   dmScreen    – array of { title, rows } objects for the DM reference screen
 *                 rows can be: { label, value } OR { header } (section divider)
 */
export const TTRPG_SYSTEMS = [
  {
    id: 'trialment',
    name: 'Trialment',
    shortName: 'Trialment',
    buttonType: 'tomato',
    buttonIcon: 'trialment',
    dmScreen: [
      {
        title: 'Ability Score Modifiers',
        rows: [
          { label: '1', value: '−5' },
          { label: '2–3', value: '−4' },
          { label: '4–5', value: '−3' },
          { label: '6–7', value: '−2' },
          { label: '8–9', value: '−1' },
          { label: '10–11', value: '+0' },
          { label: '12–13', value: '+1' },
          { label: '14–15', value: '+2' },
          { label: '16–17', value: '+3' },
          { label: '18–19', value: '+4' },
          { label: '20', value: '+5' },
        ],
      },
      {
        title: 'Difficulty Classes',
        rows: [
          { label: 'Very Easy', value: 'DC 5' },
          { label: 'Easy', value: 'DC 10' },
          { label: 'Medium', value: 'DC 15' },
          { label: 'Hard', value: 'DC 20' },
          { label: 'Very Hard', value: 'DC 25' },
          { label: 'Nearly Impossible', value: 'DC 30' },
        ],
      },
      {
        title: 'Conditions',
        rows: [
          {
            label: 'Blinded',
            value:
              'Auto-fail sight checks; attacks vs. you have Adv; your attacks have Disadv',
          },
          {
            label: 'Charmed',
            value:
              'Cannot attack charmer; charmer has Adv on social checks vs. you',
          },
          { label: 'Deafened', value: 'Auto-fail hearing checks' },
          {
            label: 'Exhaustion',
            value:
              'Levels 1-6; 1: Disadv on checks; 3: Speed halved; 5: Speed 0; 6: Death',
          },
          {
            label: 'Frightened',
            value:
              'Disadv on ability checks & attacks while source in sight; cannot move closer',
          },
          {
            label: 'Grappled',
            value:
              'Speed 0; ends if grappler incapacitated or moved out of reach',
          },
          { label: 'Incapacitated', value: 'No actions or reactions' },
          {
            label: 'Invisible',
            value: 'Attackers have Disadv; your attacks have Adv',
          },
          {
            label: 'Paralyzed',
            value:
              'Incapacitated; auto-fail STR & DEX saves; attacks have Adv; crits within 5 ft',
          },
          {
            label: 'Petrified',
            value:
              'Transformed to stone; weight ×10; incapacitated; Adv against you; immune to poison/disease',
          },
          {
            label: 'Poisoned',
            value: 'Disadv on attack rolls & ability checks',
          },
          {
            label: 'Prone',
            value:
              'Disadv on attacks; melee attacks against you have Adv; ranged attacks have Disadv',
          },
          {
            label: 'Restrained',
            value:
              'Speed 0; attacks vs. you have Adv; your attacks have Disadv; Disadv DEX saves',
          },
          {
            label: 'Stunned',
            value:
              'Incapacitated; auto-fail STR & DEX saves; attacks vs. you have Adv',
          },
          {
            label: 'Unconscious',
            value:
              'Incapacitated, prone; auto-fail STR & DEX saves; crits within 5 ft',
          },
        ],
      },
      {
        title: 'Cover',
        rows: [
          {
            label: 'Half Cover',
            value: '+2 AC & DEX saves (low wall, creature)',
          },
          {
            label: '3/4 Cover',
            value: '+5 AC & DEX saves (arrow slit, thick tree)',
          },
          { label: 'Full Cover', value: 'Cannot be targeted directly' },
        ],
      },
      {
        title: 'Action Types',
        rows: [
          {
            label: 'Action',
            value:
              'Attack, Cast, Dash, Disengage, Dodge, Help, Hide, Ready, Search, Use',
          },
          {
            label: 'Bonus Action',
            value: 'Class/spell feature; off-hand attack (Two-Weapon Fighting)',
          },
          {
            label: 'Reaction',
            value: 'Opportunity Attack, Readied action, certain spells',
          },
          {
            label: 'Movement',
            value: 'Up to speed; can split before/after action',
          },
          {
            label: 'Free',
            value: 'Interact with one object; speak a few words',
          },
        ],
      },
      {
        title: 'Exhaustion Levels',
        rows: [
          { label: 'Level 1', value: 'Disadvantage on Ability Checks' },
          { label: 'Level 2', value: 'Speed halved' },
          {
            label: 'Level 3',
            value: 'Disadvantage on Attack Rolls & Saving Throws',
          },
          { label: 'Level 4', value: 'Hit Point maximum halved' },
          { label: 'Level 5', value: 'Speed reduced to 0' },
          { label: 'Level 6', value: 'Death' },
        ],
      },
    ],
  },
  {
    id: 'cota',
    name: 'Chronicles of the Awakening',
    shortName: 'CotA',
    buttonType: 'black-white',
    buttonIcon: 'cota',
    dmScreen: [
      {
        title: 'Core Stats',
        rows: [
          {
            label: 'Might',
            value: 'Physical power; melee attacks, feats of strength',
          },
          {
            label: 'Divination',
            value: 'Arcane ability; spells, scroll checks, divine power',
          },
          {
            label: 'Agility',
            value:
              'Speed & reflexes; dodge, ranged, finesse; determines Agility Defense',
          },
          {
            label: 'Intuition',
            value:
              'Perception & wisdom; detect traps, insight, read intentions; determines Intuition Defense',
          },
        ],
      },
      {
        title: 'Defenses',
        rows: [
          {
            label: 'Agility Defense',
            value: 'Passive score vs. fast/ranged attacks and traps',
          },
          {
            label: 'Intuition Defense',
            value: 'Passive score vs. mind attacks and social manipulation',
          },
          {
            label: 'HP',
            value: 'Temporary HP absorbed first; reduced to 0 → Bloodied',
          },
          {
            label: 'Divine Influence',
            value:
              'Resource for divine abilities; replenished via rest or boons',
          },
        ],
      },
      {
        title: 'Guilds',
        rows: [
          {
            label: 'Enlightened',
            value:
              'Scholars & arcane researchers; bonuses to Divination and lore',
          },
          {
            label: 'Honorbound',
            value: 'Warriors & paladins; bonuses to Might and defence',
          },
          {
            label: 'Ironsteam',
            value: 'Engineers & artificers; craft bonuses, construct abilities',
          },
          {
            label: 'Wildheart',
            value: 'Rangers & druids; nature, tracking, beast mastery',
          },
          {
            label: '(None)',
            value: 'Unaffiliated; no guild bonus or restriction',
          },
        ],
      },
      {
        title: 'Scrolls & Titles',
        rows: [
          {
            label: 'Declared Scrolls',
            value: 'Up to 10 chosen scrolls; a character may level each once',
          },
          {
            label: 'Leveled Scrolls',
            value: 'Scrolls upgraded for enhanced effects',
          },
          { label: 'Guild Title: Member', value: 'Default rank' },
          {
            label: 'Guild Title: Librarian',
            value: 'Archivist / researcher rank',
          },
          {
            label: 'Guild Title: Ambassador',
            value: 'Diplomatic / envoy rank',
          },
          { label: 'Guild Title: Herald', value: 'Senior/champion rank' },
          { label: 'Guild Title: Archon', value: 'Leadership rank' },
        ],
      },
      {
        title: 'RP Abilities & Paths',
        rows: [
          {
            label: 'Path',
            value: 'Core archetype (e.g. Mage, Warrior, Scout)',
          },
          { label: 'Subpath', value: 'Specialisation within a Path' },
          {
            label: 'RP Ability',
            value:
              'Unique role-play power granted by Subpath at specified level',
          },
        ],
      },
    ],
  },
  {
    id: 'daggerheart',
    name: 'Daggerheart',
    shortName: 'Daggerheart',
    buttonType: 'blue',
    buttonIcon: 'daggerheart',
    dmScreen: [
      {
        title: 'Duality Dice',
        rows: [
          {
            label: 'Roll',
            value: '2d12 – one Hope die (light), one Fear die (dark)',
          },
          {
            label: 'Hope > Fear',
            value: 'Action succeeds with Hope; gain 1 Hope token',
          },
          {
            label: 'Fear > Hope',
            value: 'Action succeeds with Fear; GM gains 1 Fear token',
          },
          {
            label: 'Critical Success',
            value: 'Both dice equal the same number (doubles)',
          },
          {
            label: 'With Advantage',
            value: 'Roll an extra die of each type; keep the better pair',
          },
          {
            label: 'With Disadvantage',
            value: 'Roll an extra die of each type; keep the worse pair',
          },
        ],
      },
      {
        title: 'Core Traits',
        rows: [
          { label: 'Agility', value: 'Speed, acrobatics, stealth' },
          { label: 'Strength', value: 'Power, melee, intimidation' },
          { label: 'Finesse', value: 'Precision, ranged, sleight of hand' },
          { label: 'Instinct', value: 'Perception, survival, animal handling' },
          { label: 'Presence', value: 'Charm, leadership, performance' },
          { label: 'Knowledge', value: 'Lore, history, arcana' },
        ],
      },
      {
        title: 'Defenses & Resources',
        rows: [
          {
            label: 'Evasion',
            value: 'Passive threshold attackers must beat to hit you',
          },
          {
            label: 'Armor Score',
            value:
              'Reduce incoming damage by this amount before applying to HP',
          },
          {
            label: 'HP',
            value:
              'When reduced to 0 mark Defeat. At 3 Defeats → Out of Action',
          },
          {
            label: 'Stress',
            value:
              'Taken instead of HP or from difficult moments; max stress = Out of Action',
          },
          {
            label: 'Hope',
            value: 'Party resource; spend to activate abilities and rerolls',
          },
        ],
      },
      {
        title: 'GM Fear Tokens',
        rows: [
          {
            label: 'Gaining Fear',
            value: 'Awarded when a player rolls Fear on a Duality roll',
          },
          {
            label: 'Spending Fear',
            value:
              'Activate adversary abilities, create complications, trigger Moves',
          },
        ],
      },
      {
        title: 'Adversary Types',
        rows: [
          {
            label: 'Minion',
            value: 'Weak; often act in groups; removed by single hit',
          },
          { label: 'Standard', value: 'Regular enemy with HP and abilities' },
          {
            label: 'Solo',
            value: 'Powerful boss; multiple actions per round; high HP',
          },
        ],
      },
      {
        title: 'Domain Cards',
        rows: [
          {
            label: 'Domains',
            value:
              'Arcana, Blade, Bone, Codex, Grace, Midnight, Sage, Splendor, Valor',
          },
          {
            label: 'Class Cards',
            value: 'Chosen at character creation; determine starting abilities',
          },
          {
            label: 'Multiclassing',
            value: 'Gain a card from a secondary Domain on level-up',
          },
        ],
      },
    ],
  },
  {
    id: 'dnd5e',
    name: 'Dungeons & Dragons 5.5E',
    shortName: 'D&D 5.5E',
    buttonType: 'cinnabar',
    buttonIcon: 'dnd',
    dmScreen: [
      {
        title: 'Ability Score Modifiers',
        rows: [
          { label: '1', value: '−5' },
          { label: '2–3', value: '−4' },
          { label: '4–5', value: '−3' },
          { label: '6–7', value: '−2' },
          { label: '8–9', value: '−1' },
          { label: '10–11', value: '+0' },
          { label: '12–13', value: '+1' },
          { label: '14–15', value: '+2' },
          { label: '16–17', value: '+3' },
          { label: '18–19', value: '+4' },
          { label: '20', value: '+5' },
        ],
      },
      {
        title: 'Difficulty Classes',
        rows: [
          { label: 'Very Easy', value: 'DC 5' },
          { label: 'Easy', value: 'DC 10' },
          { label: 'Medium', value: 'DC 15' },
          { label: 'Hard', value: 'DC 20' },
          { label: 'Very Hard', value: 'DC 25' },
          { label: 'Nearly Impossible', value: 'DC 30' },
        ],
      },
      {
        title: '2024 Weapon Masteries',
        rows: [
          {
            label: 'Cleave',
            value:
              'On hit, attack second creature adjacent to first (no roll bonus)',
          },
          { label: 'Graze', value: 'On a miss, deal modifier damage' },
          { label: 'Nick', value: 'Light property attack as free interaction' },
          { label: 'Push', value: 'On hit, push target 10 ft away' },
          {
            label: 'Sap',
            value: 'On hit, target has Disadvantage on next attack roll',
          },
          {
            label: 'Slow',
            value:
              'On hit, reduce target speed by 10 ft until start of next turn',
          },
          {
            label: 'Topple',
            value: 'On hit, STR save (DC = 8 + prof + STR mod) or Prone',
          },
          {
            label: 'Vex',
            value: 'On hit, Advantage on next attack roll against same target',
          },
        ],
      },
      {
        title: 'Conditions (2024)',
        rows: [
          {
            label: 'Blinded',
            value:
              'Lose sight; Disadv on attacks; Adv against you; auto-fail sight checks',
          },
          {
            label: 'Charmed',
            value: 'Cannot attack charmer; charmer has Adv on social checks',
          },
          {
            label: 'Deafened',
            value:
              'Auto-fail hearing; Disadv on CON saves to maintain concentration',
          },
          {
            label: 'Exhaustion',
            value:
              'Level 1–10 scale; −2 to checks/saves/attacks per level; halve speed at 5; die at 10',
          },
          {
            label: 'Frightened',
            value:
              'Disadv on ability checks & attacks while source visible; cannot move closer',
          },
          { label: 'Grappled', value: 'Speed 0; still can attack and act' },
          {
            label: 'Incapacitated',
            value: 'No actions, bonus actions, or reactions',
          },
          {
            label: 'Invisible',
            value:
              'Unseen; Adv on attacks; Disadv against you; hidden unless revealed',
          },
          {
            label: 'Paralyzed',
            value: 'Incapacitated; auto-fail STR/DEX saves; crits within 5 ft',
          },
          {
            label: 'Petrified',
            value:
              'Stone; weight ×10; incapacitated; immune to poison/disease while petrified',
          },
          {
            label: 'Poisoned',
            value: 'Disadv on attack rolls & ability checks',
          },
          {
            label: 'Prone',
            value:
              'Disadv on attacks; melee Adv, ranged Disadv against you; half speed to stand',
          },
          {
            label: 'Restrained',
            value:
              'Speed 0; attacks vs. you have Adv; your attacks Disadv; Disadv DEX saves',
          },
          {
            label: 'Stunned',
            value: 'Incapacitated; auto-fail STR/DEX saves; Adv vs. you',
          },
          {
            label: 'Unconscious',
            value:
              'Incapacitated + Prone; auto-fail STR/DEX saves; crits within 5 ft',
          },
        ],
      },
      {
        title: 'Action Types',
        rows: [
          {
            label: 'Action',
            value:
              'Attack, Cast, Dash, Disengage, Dodge, Help, Hide, Influence, Magic, Ready, Search, Study, Utilise',
          },
          {
            label: 'Bonus Action',
            value:
              'Class/spell feature; off-hand attack (Two-Weapon Fighting/Nick mastery)',
          },
          {
            label: 'Reaction',
            value:
              'Opportunity Attack, Readied action, certain spells (Shield, Counterspell)',
          },
          {
            label: 'Movement',
            value:
              'Up to speed per turn; can split; costs 2× for difficult terrain',
          },
          {
            label: 'Free Interaction',
            value: 'One per turn; draw weapon, open door, pick up item',
          },
        ],
      },
    ],
  },
  {
    id: 'pf1e',
    name: 'Pathfinder 1E',
    shortName: 'Pathfinder 1E',
    buttonType: 'pathfinder',
    buttonIcon: 'pathfinder',
    dmScreen: [
      {
        title: 'Action Economy',
        rows: [
          {
            label: 'Standard Action',
            value: 'Attack, cast (most spells), activate item, total defense',
          },
          {
            label: 'Move Action',
            value:
              'Move up to speed, draw/sheathe weapon, retrieve item, stand up',
          },
          {
            label: 'Full-Round Action',
            value: 'Full attack, charge, withdraw, run, use some abilities',
          },
          {
            label: 'Swift Action',
            value: 'Certain feats/abilities; 1 per round',
          },
          {
            label: 'Immediate Action',
            value:
              'Like swift but any time, including off-turn; uses swift action next round',
          },
          {
            label: 'Free Action',
            value: 'Drop item, speak, cease concentration',
          },
        ],
      },
      {
        title: 'Armor Class',
        rows: [
          {
            label: 'AC',
            value:
              '10 + Armor + Shield + DEX + Size + Natural + Deflect + Misc',
          },
          {
            label: 'Touch AC',
            value: '10 + DEX + Size + Deflect + Misc (no armor/natural/shield)',
          },
          {
            label: 'Flat-footed AC',
            value:
              'AC without DEX or Dodge bonuses (unaware or unable to react)',
          },
          {
            label: 'CMD',
            value: '10 + BAB + STR mod + DEX mod + Size modifier',
          },
        ],
      },
      {
        title: 'Combat Maneuver Bonus (CMB)',
        rows: [
          { label: 'CMB', value: 'BAB + STR mod + Size mod' },
          {
            label: 'Grapple',
            value:
              'CMB vs CMD; Grappled: cannot move freely; Pinned: more restricted',
          },
          {
            label: 'Trip',
            value: 'CMB vs CMD; failure by 10+ = tripper falls Prone',
          },
          {
            label: 'Disarm',
            value: 'CMB vs CMD; failure by 10+ = disarmer drops weapon',
          },
          {
            label: 'Bull Rush',
            value: 'CMB vs CMD; push target straight back',
          },
          { label: 'Sunder', value: "CMB vs CMD; damage target's item" },
        ],
      },
      {
        title: 'Saving Throws',
        rows: [
          {
            label: 'Fortitude',
            value: 'CON-based; resist poison, disease, death effects',
          },
          { label: 'Reflex', value: 'DEX-based; avoid area effects, traps' },
          {
            label: 'Will',
            value: 'WIS-based; resist mind-affecting, illusions, compulsions',
          },
        ],
      },
      {
        title: 'Conditions',
        rows: [
          {
            label: 'Blinded',
            value:
              '50% miss chance; −2 AC; Disadv to hit; Adv against you; cannot use sight',
          },
          {
            label: 'Confused',
            value:
              'Random: do nothing, attack self, attack random creature, act normally',
          },
          {
            label: 'Dazed',
            value: 'No actions (but not helpless); 1 round typically',
          },
          {
            label: 'Dazzled',
            value: '−1 attack rolls & sight Perception checks',
          },
          {
            label: 'Exhausted',
            value:
              '−6 STR & DEX; half speed; becomes Fatigued after 1 hour rest',
          },
          {
            label: 'Fatigued',
            value:
              '−2 STR & DEX; cannot run or charge; becomes Exhausted if Fatigued again',
          },
          {
            label: 'Frightened',
            value:
              '−2 attacks/checks/saves/skills; must flee; stacks with Shaken',
          },
          {
            label: 'Grappled',
            value:
              '−2 attack (not vs grappler); cannot cast most spells; move only with CMB/grapple',
          },
          { label: 'Paralyzed', value: 'STR/DEX → 0; helpless; cannot act' },
          {
            label: 'Pinned',
            value:
              'Helpless against grappler; −4 AC vs others; cannot use most abilities',
          },
          {
            label: 'Prone',
            value: '−4 melee attacks; melee vs you +4; ranged vs you −4',
          },
          {
            label: 'Shaken',
            value: '−2 attacks, saves, skill checks, ability checks',
          },
          {
            label: 'Sickened',
            value: '−2 attacks, saves, skill checks, damage rolls',
          },
          {
            label: 'Staggered',
            value: 'Only one standard or move action per round (not both)',
          },
          {
            label: 'Stunned',
            value:
              'Cannot take actions; drop all held items; −2 AC; attackers get flanking bonus',
          },
        ],
      },
    ],
  },
  {
    id: 'pf2e',
    name: 'Pathfinder 2E',
    shortName: 'Pathfinder 2E',
    buttonType: 'pathfinder2',
    buttonIcon: 'pathfinder2',
    dmScreen: [
      {
        title: 'Action Economy',
        rows: [
          {
            label: 'Actions',
            value: '3 per turn; spend on activities & single-action abilities',
          },
          {
            label: 'Reaction',
            value:
              '1 per round; triggered by specific events (e.g. Attack of Opportunity)',
          },
          { label: 'Free Action', value: 'No cost; usually once per trigger' },
          {
            label: 'Common Actions (1)',
            value: 'Interact, Step, Release, Recall Knowledge, Seek',
          },
          {
            label: 'Common Actions (2)',
            value: 'Stride, Strike, Raise Shield',
          },
          {
            label: 'Common Actions (3)',
            value:
              'Arrest a Fall (Reaction); most class powers use 1–3 actions',
          },
        ],
      },
      {
        title: 'Degrees of Success',
        rows: [
          {
            label: 'Critical Success',
            value: 'Beat DC by 10+ or natural 20 that succeeds',
          },
          { label: 'Success', value: 'Meet or beat DC' },
          { label: 'Failure', value: 'Miss DC by 1–9' },
          {
            label: 'Critical Failure',
            value: 'Miss DC by 10+ or natural 1 that fails',
          },
          {
            label: 'Note',
            value:
              'Natural 20 shifts result one step better; natural 1 shifts one step worse',
          },
        ],
      },
      {
        title: 'Proficiency Ranks',
        rows: [
          { label: 'Untrained', value: 'Level + 0 (no proficiency bonus)' },
          { label: 'Trained', value: 'Level + 2' },
          { label: 'Expert', value: 'Level + 4' },
          { label: 'Master', value: 'Level + 6' },
          { label: 'Legendary', value: 'Level + 8' },
        ],
      },
      {
        title: 'Armor Class & Saves',
        rows: [
          {
            label: 'AC',
            value: '10 + DEX (cap) + armor proficiency + item bonus + level',
          },
          {
            label: 'Fortitude',
            value: 'CON + proficiency; resist physical afflictions',
          },
          {
            label: 'Reflex',
            value: 'DEX + proficiency; avoid explosions, traps',
          },
          { label: 'Will', value: 'WIS + proficiency; resist mental effects' },
        ],
      },
      {
        title: 'Conditions with Values',
        rows: [
          {
            label: 'Clumsy X',
            value:
              '−X to Dex-based DCs & checks; X reduces by 1 each round unless stated',
          },
          {
            label: 'Drained X',
            value:
              '−X to CON modifier; max HP reduced; persistent unless treated',
          },
          {
            label: 'Enfeebled X',
            value: '−X to STR-based attack/damage/saves',
          },
          {
            label: 'Frightened X',
            value: '−X to all checks & DCs; X reduces by 1 at end of each turn',
          },
          {
            label: 'Sickened X',
            value: '−X to all checks & DCs; spend action to reduce with save',
          },
          { label: 'Slowed X', value: 'Lose X actions on next turn' },
          { label: 'Stunned X', value: 'Lose X actions total (across turns)' },
          {
            label: 'Wounded X',
            value: 'Dying threshold lowered by X; removed by full HP heal',
          },
        ],
      },
      {
        title: 'Death & Dying',
        rows: [
          {
            label: 'Dying 0 (Knocked Out)',
            value: 'Unconscious; if hit again go to Dying 1',
          },
          {
            label: 'Dying 1–3',
            value: 'Unconscious; roll Recovery (flat DC 11 + Dying value)',
          },
          { label: 'Dying 4', value: 'Dead (modified by Wounded value)' },
          {
            label: 'Recovery Roll',
            value:
              'Crit success: −2 Dying; Success: −1 Dying; Failure: +1 Dying; Crit Fail: +2 Dying',
          },
        ],
      },
    ],
  },
  {
    id: 'sf1e',
    name: 'Starfinder 1E',
    shortName: 'Starfinder 1E',
    buttonType: 'starfinder',
    buttonIcon: 'starfinder',
    dmScreen: [
      {
        title: 'Vital Statistics',
        rows: [
          {
            label: 'Stamina Points (SP)',
            value:
              'First to take damage; fully restored by 10-min rest with 1 Resolve Point',
          },
          {
            label: 'Hit Points (HP)',
            value:
              'Durable health; only restored by medicine, magic, or full rest',
          },
          {
            label: 'Resolve Points (RP)',
            value:
              'Powerful resource: stabilize (1 RP), restore SP (1 RP), use class abilities',
          },
        ],
      },
      {
        title: 'Armor Class',
        rows: [
          {
            label: 'EAC',
            value:
              'Energy Armor Class; vs. laser, fire, electricity, cold, etc.',
          },
          {
            label: 'KAC',
            value:
              'Kinetic Armor Class; vs. bullets, blades, bludgeon, acid splash',
          },
          { label: 'CMD', value: 'KAC + 8; used to resist combat maneuvers' },
        ],
      },
      {
        title: 'Weapon Categories',
        rows: [
          {
            label: 'Small Arms',
            value: 'One-handed; d4 base; low bulk; good at short range',
          },
          {
            label: 'Longarms',
            value: 'Two-handed; d8 base; medium range; most versatile',
          },
          {
            label: 'Heavy Weapons',
            value: 'Two-handed; high damage; proficiency required; high bulk',
          },
          {
            label: 'Snipers',
            value:
              'Two-handed; extreme range; usually fire once; scope optional',
          },
          {
            label: 'Melee – Operative',
            value: 'Light; used with Operative Exploit for trick attacks',
          },
          {
            label: 'Melee – Basic',
            value: 'Most melee; STR modifier to hit and damage',
          },
          {
            label: 'Grenades',
            value: 'Single use; thrown (range 20 ft); AoE; KAC target',
          },
        ],
      },
      {
        title: 'Special Properties',
        rows: [
          {
            label: 'Analog',
            value: 'Immune to hacking & targeting by tech-based abilities',
          },
          {
            label: 'Arc',
            value: 'On hit, arcs to adjacent creature for set damage',
          },
          {
            label: 'Blast',
            value: 'Cone attack; everyone in area makes Reflex save',
          },
          {
            label: 'Boost',
            value:
              'Spend 1 full action to boost; next attack has enhanced damage',
          },
          { label: 'Bright', value: 'Illuminates area on hit (blinds robots)' },
          {
            label: 'Burn X',
            value: 'On hit, burning condition – X fire damage/round',
          },
          {
            label: 'Stun',
            value: 'On hit, target makes FORT save or is stunned',
          },
          {
            label: 'Unwieldy',
            value:
              'Cannot make iterative attacks; cannot use with Trick Attack',
          },
        ],
      },
      {
        title: 'Action Economy',
        rows: [
          {
            label: 'Standard Action',
            value: 'Single attack, activate ability, full defense',
          },
          {
            label: 'Move Action',
            value: 'Move up to speed, manipulate item, stand up',
          },
          {
            label: 'Full Action',
            value: 'Full attack, charge, run, some special abilities',
          },
          {
            label: 'Swift Action',
            value: '1 per round; certain boosts/abilities',
          },
          { label: 'Reaction', value: '1 per round; AoO, certain abilities' },
        ],
      },
    ],
  },
  {
    id: 'sf2e',
    name: 'Starfinder 2E',
    shortName: 'Starfinder 2E',
    buttonType: 'starfinder2',
    buttonIcon: 'starfinder2',
    dmScreen: [
      {
        title: 'Action Economy',
        rows: [
          {
            label: 'Actions',
            value: '3 per turn; same as Pathfinder 2E foundation',
          },
          { label: 'Reaction', value: '1 per round; triggered abilities' },
          {
            label: 'Free Action',
            value: 'No cost; usually limited by frequency',
          },
          {
            label: 'Strike',
            value: '1 action; each extra Strike this turn gains −5 MAP',
          },
          { label: 'Stride', value: '1 action; move up to speed' },
          {
            label: 'Interact',
            value: '1 action; draw weapon, use item, open door',
          },
        ],
      },
      {
        title: 'Degrees of Success',
        rows: [
          {
            label: 'Critical Success',
            value: 'Beat DC by 10+ or natural 20 that succeeds',
          },
          { label: 'Success', value: 'Meet or beat DC' },
          { label: 'Failure', value: 'Miss DC by 1–9' },
          {
            label: 'Critical Failure',
            value: 'Miss DC by 10+ or natural 1 that fails',
          },
        ],
      },
      {
        title: 'Stamina & Resolve (2E)',
        rows: [
          {
            label: 'Stamina Points',
            value:
              'Absorbed before HP; restored via 10-min rest (costs 1 Resolve)',
          },
          {
            label: 'Hit Points',
            value: 'Long-term durability; need treatment or magic to restore',
          },
          {
            label: 'Resolve Points',
            value:
              'Powerful resource; spent on Stamina recovery, Resolve abilities, stabilization',
          },
        ],
      },
      {
        title: 'Key Defenses',
        rows: [
          {
            label: 'AC',
            value: '10 + armor + proficiency + DEX (cap) + level',
          },
          { label: 'Fortitude', value: 'CON-based; resist physical threats' },
          { label: 'Reflex', value: 'DEX-based; dodge explosions, traps' },
          { label: 'Will', value: 'WIS-based; resist mind-affecting' },
        ],
      },
      {
        title: 'Technology & Equipment',
        rows: [
          {
            label: 'Bulk',
            value: 'Encumbrance system; Light (L) items do not count until 10',
          },
          {
            label: 'Item Level',
            value:
              'Equipment power tier (1–20); access depends on availability',
          },
          {
            label: 'Upgrades',
            value: 'Armor & weapon slots for modifications',
          },
          {
            label: 'Charges',
            value: 'Energy weapons have limited shots; reload unit = Battery',
          },
          {
            label: 'Analog',
            value: 'Immune to tech-based hacking/interference',
          },
        ],
      },
    ],
  },
  {
    id: 'coc7e',
    name: 'Call of Cthulhu 7E',
    shortName: 'Call of Cthulhu',
    buttonType: 'onyx',
    buttonIcon: 'death',
    dmScreen: [
      {
        title: 'Core Characteristics',
        rows: [
          {
            label: 'STR',
            value: 'Physical strength; melee damage bonus, climb, jump',
          },
          { label: 'CON', value: 'Constitution; HP & Sanity Pool at creation' },
          {
            label: 'SIZ',
            value: 'Size (mass); affects damage bonus and HP calculation',
          },
          {
            label: 'DEX',
            value: 'Dexterity; initiative order, Dodge, fine manipulation',
          },
          {
            label: 'APP',
            value: 'Appearance; social interactions, first impressions',
          },
          {
            label: 'INT',
            value: 'Intelligence; Idea Roll (INT × 5), Languages, Lore',
          },
          {
            label: 'POW',
            value:
              'Willpower/spirit; Sanity starting value = POW × 5; Magic Points = POW / 5',
          },
          {
            label: 'EDU',
            value:
              'Education; Know Roll (EDU × 5); starting Occupation skill points',
          },
          {
            label: 'Luck',
            value:
              'Push roll resource; can be spent to modify results or re-roll',
          },
        ],
      },
      {
        title: 'Derived Stats',
        rows: [
          {
            label: 'HP',
            value: '(CON + SIZ) / 10 rounded down (Major Wound at HP / 2)',
          },
          {
            label: 'Magic Points (MP)',
            value: 'POW / 5; regenerates after rest; used to cast spells',
          },
          {
            label: 'Sanity (SAN)',
            value:
              'Starts at POW × 5 (max 99 − Cthulhu Mythos skill); see Sanity Loss table',
          },
          {
            label: 'Build',
            value:
              'STR+SIZ: 2–12 → −2 Build; 13–16 → −1; 17–24 → 0; 25–32 → +1; 33–40 → +2',
          },
          {
            label: 'Damage Bonus',
            value:
              'Build −2 → −2; Build −1 → −1d4; Build 0 → None; Build +1 → +1d4; Build +2 → +1d6',
          },
          {
            label: 'Move Rate',
            value:
              'STR or DEX < SIZ → 7; both equal → 8; both > SIZ → 9; Age reduces by 1 per decade after 40',
          },
        ],
      },
      {
        title: 'Skill Rolls & Difficulty',
        rows: [
          { label: 'Regular', value: 'Roll ≤ skill value on d100' },
          { label: 'Hard', value: 'Roll ≤ skill ÷ 2' },
          { label: 'Extreme', value: 'Roll ≤ skill ÷ 5' },
          {
            label: 'Opposed Roll',
            value:
              'Both roll; highest success level wins; ties go to higher skill',
          },
          {
            label: 'Bonus Die',
            value: 'Roll an extra tens die, take the lower result',
          },
          {
            label: 'Penalty Die',
            value: 'Roll an extra tens die, take the higher result',
          },
          {
            label: 'Fumble',
            value:
              '01 always succeeds; 96–00 fumble if skill < 50; 100 always fumbles',
          },
        ],
      },
      {
        title: 'Pushing a Roll',
        rows: [
          {
            label: 'When',
            value:
              'After a failed roll; Keeper must agree; logical repeat only',
          },
          { label: 'Success', value: 'Succeed as normal' },
          {
            label: 'Failure',
            value:
              'Serious consequence decided by Keeper (injury, lost item, witness, etc.)',
          },
          {
            label: 'Restriction',
            value: 'Cannot Push attacks, Sanity rolls, or Luck rolls',
          },
        ],
      },
      {
        title: 'Sanity Loss',
        rows: [
          { label: 'Minor (0/1d3)', value: 'Strange sound, odd smell, unease' },
          { label: 'Low (0/1d4)', value: 'Unsettling vision or event' },
          { label: 'Medium (1/1d6)', value: 'Disturbing creature or death' },
          {
            label: 'High (1d3/1d10)',
            value: 'Monstrous entity, horrific scene',
          },
          {
            label: 'Extreme (1d6/1d20)',
            value: 'Greater Old One glimpse, mass slaughter',
          },
          {
            label: 'Temporary Insanity',
            value:
              'Lose ≥ 5 SAN in one scene → Keeper inflicts bout of madness',
          },
          {
            label: 'Indefinite Insanity',
            value:
              'Lose ≥ 1/5 of current SAN in one session → long-term mental affliction',
          },
          {
            label: 'Permanent Insanity',
            value: 'SAN reaches 0 → character is lost to madness',
          },
        ],
      },
      {
        title: 'Combat',
        rows: [
          {
            label: 'Initiative',
            value: 'High DEX goes first; ties broken by DEX stat',
          },
          {
            label: 'Fighting',
            value:
              'Roll Fighting (Brawl or weapon skill) vs Hard Dodge or Hard opponent skill',
          },
          {
            label: 'Firearms',
            value:
              'Standard roll at regular range; Hard at long range; opposed Dodge impossible vs gunfire',
          },
          {
            label: 'Dodge',
            value:
              'DEX-based; can Dodge melee/thrown; cannot fully dodge aimed gunfire',
          },
          {
            label: 'Fighting Back',
            value: 'Instead of Dodge; both roll; winner deals damage',
          },
          {
            label: 'Major Wound',
            value:
              'Single hit ≥ HP / 2 → CON roll or fall unconscious; check again at end of round',
          },
          {
            label: 'Dying',
            value:
              'HP 0 or lower → dying; First Aid (1 HP) or Medicine within hour or dead',
          },
        ],
      },
    ],
  },
];

export const getSystemById = (id) =>
  TTRPG_SYSTEMS.find((s) => s.id === id) || null;
